import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { ACCESS_TYPES, resolveOrgAccess } from '@src/packages/utils/access';

import type { ActiveUserDashboardReduxState } from '../types';

const ORG_WIDE_ACCESS: string[] = [ACCESS_TYPES.OWNER, ACCESS_TYPES.ADMIN, ACCESS_TYPES.READER];
const DIVISION_ACCESS: string[] = [ACCESS_TYPES.ADMIN, ACCESS_TYPES.EDITOR, ACCESS_TYPES.READER];

export interface OrgAccess {
  isAdmin: boolean;
  /** Divisions the current user can read (empty for org-wide roles). */
  accessibleDivisionIds: string[];
  /** Stable key for effect dependencies. */
  accessKey: string;
}

/**
 * Resolves whether the current user has org-wide read access or is limited to
 * specific divisions. Shared by the roster and RTDB hooks so both use the same
 * query strategy.
 */
export const useOrgAccess = (orgId: string): OrgAccess => {
  const claims = useSelector(
    ({ user }: ActiveUserDashboardReduxState) => user.access.data?.claims,
  );
  const orgUserAccess = useSelector(
    ({ user }: ActiveUserDashboardReduxState) => user.orgUser?.data?.access,
  );
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions = useSelector(
    ({ divisions }: ActiveUserDashboardReduxState) => divisions.assigned.data.divisions,
  );

  return useMemo(() => {
    const isAdmin = Boolean(userOrgAccess && ORG_WIDE_ACCESS.includes(userOrgAccess));

    const accessibleDivisionIds = isAdmin
      ? []
      : Object.entries(userOrgDivisions ?? {})
          .filter(([, divData]) => Boolean(divData?.access && DIVISION_ACCESS.includes(divData.access)))
          .map(([divId]) => divId)
          .sort();

    return {
      isAdmin,
      accessibleDivisionIds,
      accessKey: `${orgId}|${isAdmin ? 'admin' : accessibleDivisionIds.join(',')}`,
    };
  }, [orgId, userOrgAccess, userOrgDivisions]);
};
